import { randomBelow } from "../../common/random";
import { md5, sha1, sha3 } from "./digests";
import { LOCAL_DOMAIN_CODES } from "./types";

export function generateUUIDv4(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  const hex = "0123456789abcdef";
  const a = new Uint8Array(16);
  crypto.getRandomValues(a);
  a[6] = (a[6] & 0x0f) | 0x40; // version 4
  a[8] = (a[8] & 0x3f) | 0x80; // variant 1
  let id = "";
  for (let i = 0; i < 16; i++) {
    id += hex[a[i] >> 4] + hex[a[i] & 15];
    if (i === 3 || i === 5 || i === 7 || i === 9) id += "-";
  }
  return id;
}

// One clock for v1, v2 and v6; RFC 9562 moves the clock sequence only when that clock is seen to go back.
const TICKS_PER_MS = 10000;
let lastTime = -1;
let ticks = 0;
let clockSequence = crypto.getRandomValues(new Uint16Array(1))[0] & 0x3fff;
const node = new Uint8Array(6);
crypto.getRandomValues(node);
node[0] |= 0x01; // Set multicast bit to indicate random node

// The clock only reads milliseconds, so IDs sharing one count up through its 100 ns ticks rather than repeat a time.
function getG1582(): { time: bigint; seq: number } {
  let now = Date.now();
  if (now === lastTime) {
    ticks++;
    // Every tick of the millisecond spent, the rest of a batch waits it out rather than stamp a time still to come.
    while (ticks === TICKS_PER_MS && now <= lastTime) now = Date.now();
  } else if (now < lastTime) {
    clockSequence = (clockSequence + 1) & 0x3fff;
  }
  if (now !== lastTime) ticks = 0;
  lastTime = now;
  const time = BigInt(now) * 10000n + 122192928000000000n + BigInt(ticks); // 100-ns since 1582
  return { time, seq: clockSequence };
}

export function toHex(bytes: Uint8Array): string {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function formatUUID(bytes: Uint8Array): string {
  const hex = toHex(bytes);
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}

export function bytesToBigInt(bytes: Uint8Array): bigint {
  let value = 0n;
  for (const byte of bytes) value = (value << 8n) | BigInt(byte);
  return value;
}

// Reads the value five bits at a time from the bottom, so a short value keeps the leading digits of the alphabet.
export function toBase32(value: bigint, digits: number, alphabet: string): string {
  let text = "";
  for (let i = 0; i < digits; i++) {
    text = alphabet[Number(value & 31n)] + text;
    value >>= 5n;
  }
  return text;
}

function writeUint32BE(bytes: Uint8Array, offset: number, value: number): void {
  bytes[offset] = (value >>> 24) & 0xff;
  bytes[offset + 1] = (value >>> 16) & 0xff;
  bytes[offset + 2] = (value >>> 8) & 0xff;
  bytes[offset + 3] = value & 0xff;
}

function writeUint24BE(bytes: Uint8Array, offset: number, value: number): void {
  bytes[offset] = (value >>> 16) & 0xff;
  bytes[offset + 1] = (value >>> 8) & 0xff;
  bytes[offset + 2] = value & 0xff;
}

export function generateUUIDv1(): string {
  const { time, seq } = getG1582();
  const bytes = new Uint8Array(16);
  const timeMid = Number((time >> 32n) & 0xffffn);
  const timeHi = Number((time >> 48n) & 0x0fffn) | 0x1000; // version 1

  writeUint32BE(bytes, 0, Number(time & 0xffffffffn));
  bytes[4] = timeMid >>> 8;
  bytes[5] = timeMid & 0xff;
  bytes[6] = timeHi >>> 8;
  bytes[7] = timeHi & 0xff;
  bytes[8] = (seq >>> 8) | 0x80;
  bytes[9] = seq & 0xff;
  bytes.set(node, 10);
  return formatUUID(bytes);
}

// DCE Security spends the low half of the timestamp on a local ID and the low clock sequence byte on its domain.
export function generateUUIDv2(localId: number, domain: string): string {
  const { time, seq } = getG1582();
  const bytes = new Uint8Array(16);
  const timeMid = Number((time >> 32n) & 0xffffn);
  const timeHi = Number((time >> 48n) & 0x0fffn) | 0x2000; // version 2

  writeUint32BE(bytes, 0, localId);
  bytes[4] = timeMid >>> 8;
  bytes[5] = timeMid & 0xff;
  bytes[6] = timeHi >>> 8;
  bytes[7] = timeHi & 0xff;
  bytes[8] = (seq >>> 8) | 0x80;
  bytes[9] = LOCAL_DOMAIN_CODES[domain] ?? 0;
  // Six bits of clock sequence is all v2 keeps, so a batch would repeat itself if the node stayed put as well.
  crypto.getRandomValues(bytes.subarray(10));
  bytes[10] |= 0x01; // multicast, as for the node above
  return formatUUID(bytes);
}

export function generateUUIDv6(): string {
  const { time, seq } = getG1582();
  const bytes = new Uint8Array(16);
  const timeMid = Number((time >> 12n) & 0xffffn);
  const timeLow = Number(time & 0x0fffn) | 0x6000; // version 6

  writeUint32BE(bytes, 0, Number((time >> 28n) & 0xffffffffn));
  bytes[4] = timeMid >>> 8;
  bytes[5] = timeMid & 0xff;
  bytes[6] = timeLow >>> 8;
  bytes[7] = timeLow & 0xff;
  bytes[8] = (seq >>> 8) | 0x80;
  bytes[9] = seq & 0xff;
  bytes.set(node, 10);
  return formatUUID(bytes);
}

export function generateUUIDv7(): string {
  return formatUUID(uuidV7Bytes());
}

// RFC 9562's first method: rand_a and the top 30 bits of rand_b count up from a random start each millisecond.
const V7_COUNTER_LIMIT = 2 ** 42;
let v7LastTime = -1;
let v7Counter = 0;

// Kept apart from the formatting because a TypeID is this same layout, encoded as base32 rather than hex.
function uuidV7Bytes(): Uint8Array {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  let time = Date.now();
  if (time === v7LastTime) {
    v7Counter++;
    // Out of counter, the rest of a batch waits the millisecond out rather than stamp a time still to come.
    while (v7Counter === V7_COUNTER_LIMIT && time <= v7LastTime) time = Date.now();
  }
  // 41 random bits under a clear top one, the guard the RFC suggests so a batch cannot roll the counter over.
  if (time !== v7LastTime) v7Counter = randomBelow(2 ** 21) * 2 ** 20 + randomBelow(2 ** 20);
  v7LastTime = time;

  bytes[0] = Math.floor(time / 2 ** 40) & 0xff;
  bytes[1] = Math.floor(time / 2 ** 32) & 0xff;
  bytes[2] = Math.floor(time / 2 ** 24) & 0xff;
  bytes[3] = Math.floor(time / 2 ** 16) & 0xff;
  bytes[4] = Math.floor(time / 2 ** 8) & 0xff;
  bytes[5] = time & 0xff;

  bytes[6] = 0x70 | Math.floor(v7Counter / 2 ** 38); // version 7
  bytes[7] = Math.floor(v7Counter / 2 ** 30) & 0xff;
  bytes[8] = 0x80 | (Math.floor(v7Counter / 2 ** 24) & 0x3f); // variant 1
  bytes[9] = Math.floor(v7Counter / 2 ** 16) & 0xff;
  bytes[10] = Math.floor(v7Counter / 2 ** 8) & 0xff;
  bytes[11] = v7Counter & 0xff;
  return bytes;
}

// Every bit outside the version and variant is the vendor's to define; with no scheme to follow they are random.
export function generateUUIDv8(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x80; // version 8
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 1
  return formatUUID(bytes);
}

export function generateNanoID(size = 21): string {
  const urlAlphabet = "useandom-26T198340PX75pxJACKVERYMINDBUSHWOLF_GQZbfghjklqvwyzrict";
  let id = "";
  const bytes = crypto.getRandomValues(new Uint8Array(size));
  for (let i = 0; i < size; i++) {
    id += urlAlphabet[bytes[i] & 63];
  }
  return id;
}

const CUID_BLOCK_SIZE = 4;
const CUID_BLOCK_VALUES = 36 ** CUID_BLOCK_SIZE;
// The fingerprint separates generating processes; a browser has no pid or hostname, so a block is drawn per load.
const cuidFingerprint = cuidBlock();
let cuidCounter = 0;

export function generateCUID(): string {
  const counter = cuidCounter.toString(36).padStart(CUID_BLOCK_SIZE, "0");
  cuidCounter = (cuidCounter + 1) % CUID_BLOCK_VALUES;
  return `c${Date.now().toString(36)}${counter}${cuidFingerprint}${cuidBlock()}${cuidBlock()}`;
}

function cuidBlock(): string {
  return randomBelow(CUID_BLOCK_VALUES).toString(36).padStart(CUID_BLOCK_SIZE, "0");
}

const CUID2_LENGTH = 24;
const CUID2_LETTERS = "abcdefghijklmnopqrstuvwxyz";
const CUID2_FIRST_COUNT = 476782367; // the ceiling CUID2 draws its opening counter below
// Keccak's constants are declared further down the file, so the fingerprint is hashed on first use, not at load.
let cuid2Fingerprint = "";
let cuid2Counter = randomBelow(CUID2_FIRST_COUNT);

// Hashing the time, salt, counter and fingerprint together is what leaves a CUID2 opaque instead of sequential.
export function generateCUID2(): string {
  cuid2Fingerprint ||= cuid2Hash(cuid2Entropy(32)).slice(0, 32);
  const time = Date.now().toString(36);
  const count = (cuid2Counter++).toString(36);
  const hashed = cuid2Hash(time + cuid2Entropy(CUID2_LENGTH) + count + cuid2Fingerprint);
  // A leading letter, because an ID that can start with a digit is not a valid name in most of the places IDs go.
  return CUID2_LETTERS[randomBelow(CUID2_LETTERS.length)] + hashed.slice(1, CUID2_LENGTH);
}

// The digest read as one base-36 number, less its first digit: that one cannot reach the top of the alphabet.
function cuid2Hash(input: string): string {
  return bytesToBigInt(sha3(stringToUTF8Bytes(input))).toString(36).slice(1);
}

function cuid2Entropy(length: number): string {
  let entropy = "";
  while (entropy.length < length) entropy += randomBelow(36).toString(36);
  return entropy;
}

const ENCODING = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const ENCODING_LEN = ENCODING.length;
const ulidRandom = new Uint8Array(16);
let ulidLastTime = -1;

export function generateULID(): string {
  let now = Date.now();
  // The spec's monotonic mode: inside one millisecond the random part is incremented rather than redrawn.
  if (now === ulidLastTime) {
    let i = ulidRandom.length - 1;
    while (i >= 0 && ulidRandom[i] === ENCODING_LEN - 1) ulidRandom[i--] = 0;
    if (i >= 0) ulidRandom[i]++;
    // All 80 bits spent is a failure to the spec; the rest of a batch waits the millisecond out instead.
    while (i < 0 && now <= ulidLastTime) now = Date.now();
  }
  if (now !== ulidLastTime) {
    crypto.getRandomValues(ulidRandom);
    for (let i = 0; i < ulidRandom.length; i++) ulidRandom[i] %= ENCODING_LEN;
  }
  ulidLastTime = now;

  let timeStr = "";
  let t = now;
  for (let i = 0; i < 10; i++) {
    const mod = t % ENCODING_LEN;
    timeStr = ENCODING.charAt(mod) + timeStr;
    t = (t - mod) / ENCODING_LEN;
  }

  let randomStr = "";
  for (const digit of ulidRandom) randomStr += ENCODING.charAt(digit);

  return timeStr + randomStr;
}

const BASE62 = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
export function generateKSUID(): string {
  const payload = new Uint8Array(16);
  crypto.getRandomValues(payload);
  const time = Math.floor(Date.now() / 1000) - 1400000000;

  const buffer = new Uint8Array(20);
  buffer[0] = (time >>> 24) & 0xff;
  buffer[1] = (time >>> 16) & 0xff;
  buffer[2] = (time >>> 8) & 0xff;
  buffer[3] = time & 0xff;
  buffer.set(payload, 4);

  // Convert bytes to Base62
  let value = 0n;
  for (let i = 0; i < buffer.length; i++) {
    value = (value * 256n) + BigInt(buffer[i]);
  }

  let result = "";
  while (value > 0n) {
    const rem = Number(value % 62n);
    result = BASE62[rem] + result;
    value /= 62n;
  }
  return result.padStart(27, "0");
}

const XID_ALPHABET = "0123456789abcdefghijklmnopqrstuv";
const xidMachine = crypto.getRandomValues(new Uint8Array(3));
const xidProcess = crypto.getRandomValues(new Uint16Array(1))[0];
let xidCounter = randomBelow(0x1000000);

export function generateXID(): string {
  const bytes = new Uint8Array(12);
  writeUint32BE(bytes, 0, Math.floor(Date.now() / 1000));
  bytes.set(xidMachine, 4);
  bytes[7] = xidProcess >>> 8;
  bytes[8] = xidProcess & 0xff;
  xidCounter = (xidCounter + 1) & 0xffffff;
  writeUint24BE(bytes, 9, xidCounter);
  // 96 bits across 20 base32 characters leaves four spare, and XID pads them onto the end rather than the front.
  return toBase32(bytesToBigInt(bytes) << 4n, 20, XID_ALPHABET);
}

const CROCKFORD_ALPHABET = "0123456789abcdefghjkmnpqrstvwxyz";

// A TypeID is a UUIDv7 in Crockford base32, so 128 bits fill 26 characters with the first holding only three.
export function generateTypeID(prefix: string): string {
  const suffix = toBase32(bytesToBigInt(uuidV7Bytes()), 26, CROCKFORD_ALPHABET);
  return prefix ? `${prefix}_${suffix}` : suffix;
}

// Since MongoDB 3.4 the middle five bytes are random per process rather than a machine ID and a process ID.
const objectIdRandom = crypto.getRandomValues(new Uint8Array(5));
let objectIdCounter = randomBelow(0x1000000);

export function generateObjectId(): string {
  const bytes = new Uint8Array(12);
  writeUint32BE(bytes, 0, Math.floor(Date.now() / 1000));
  bytes.set(objectIdRandom, 4);
  objectIdCounter = (objectIdCounter + 1) & 0xffffff;
  writeUint24BE(bytes, 9, objectIdCounter);
  return toHex(bytes);
}

const PUSH_ALPHABET = "-0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ_abcdefghijklmnopqrstuvwxyz";
const pushRandom = new Uint8Array(12);
let pushLastTime = 0;

export function generatePushID(): string {
  const now = Date.now();
  let remaining = now;
  let id = "";
  for (let i = 0; i < 8; i++) {
    id = PUSH_ALPHABET[remaining % 64] + id;
    remaining = Math.floor(remaining / 64);
  }

  // Inside one millisecond the random block is incremented rather than redrawn, so a burst still sorts in order.
  if (now === pushLastTime) {
    let i = pushRandom.length - 1;
    while (i >= 0 && pushRandom[i] === 63) pushRandom[i--] = 0;
    if (i >= 0) pushRandom[i]++;
  } else {
    crypto.getRandomValues(pushRandom);
    for (let i = 0; i < pushRandom.length; i++) pushRandom[i] &= 63;
  }
  pushLastTime = now;

  for (const value of pushRandom) id += PUSH_ALPHABET[value];
  return id;
}

let sfSequence = 0n;
let sfLastTime = -1n;
const sfMachineId = 1n; // Mock machine ID
export function generateSnowflake(): string {
  const epoch = 1288834974657n; // Twitter Snowflake epoch
  let time = BigInt(Date.now());

  if (time === sfLastTime) {
    sfSequence = (sfSequence + 1n) & 4095n;
    if (sfSequence === 0n) {
      while (time <= sfLastTime) {
        time = BigInt(Date.now());
      }
    }
  } else {
    sfSequence = 0n;
  }

  sfLastTime = time;

  const id = ((time - epoch) << 22n) | (sfMachineId << 12n) | sfSequence;
  return id.toString();
}

// Sonyflake counts 10 ms ticks from 2014-09-01, then spends 8 bits on the sequence and 16 on the machine.
const SONYFLAKE_EPOCH = Date.UTC(2014, 8, 1);
const sonyflakeMachine = BigInt(crypto.getRandomValues(new Uint16Array(1))[0]);
let sonyflakeSequence = 0;
let sonyflakeLastTick = -1n;

export function generateSonyflake(): string {
  let tick = sonyflakeTick();
  if (tick === sonyflakeLastTick) {
    sonyflakeSequence = (sonyflakeSequence + 1) & 0xff;
    // Only 256 IDs fit in a tick, so the rest of a batch waits the tick out rather than repeat one.
    while (sonyflakeSequence === 0 && tick <= sonyflakeLastTick) tick = sonyflakeTick();
  } else {
    sonyflakeSequence = 0;
  }

  sonyflakeLastTick = tick;
  return ((tick << 24n) | (BigInt(sonyflakeSequence) << 16n) | sonyflakeMachine).toString();
}

function sonyflakeTick(): bigint {
  return BigInt(Math.floor((Date.now() - SONYFLAKE_EPOCH) / 10));
}

export function parseUUID(uuid: string): Uint8Array {
  const hex = uuid.replace(/-/g, "");
  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i++) {
    bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

export function stringToUTF8Bytes(str: string): Uint8Array {
  return new TextEncoder().encode(str);
}

export function generateUUIDv3(name: string, namespace: string): string {
  const nsBytes = parseUUID(namespace);
  const nameBytes = stringToUTF8Bytes(name);
  const bytes = new Uint8Array(16 + nameBytes.length);
  bytes.set(nsBytes, 0);
  bytes.set(nameBytes, 16);

  const hash = md5(bytes);
  hash[6] = (hash[6] & 0x0f) | 0x30; // version 3
  hash[8] = (hash[8] & 0x3f) | 0x80; // variant 1
  return formatUUID(hash);
}

export function generateUUIDv5(name: string, namespace: string): string {
  const nsBytes = parseUUID(namespace);
  const nameBytes = stringToUTF8Bytes(name);
  const bytes = new Uint8Array(16 + nameBytes.length);
  bytes.set(nsBytes, 0);
  bytes.set(nameBytes, 16);

  const hash = sha1(bytes);
  hash[6] = (hash[6] & 0x0f) | 0x50; // version 5
  hash[8] = (hash[8] & 0x3f) | 0x80; // variant 1

  // SHA-1 is 20 bytes, UUID needs 16
  return formatUUID(hash.slice(0, 16));
}
